import { useQuery, UseQueryResult } from '@tanstack/react-query';

import {
    IndicatorT,
    LibraryChartTemplateT,
    VizType,
} from 'business/definitions';
import { RQAPICallWithData } from 'store/APIClient';
import { IndicatorFilterT } from '../helpers/types';

export const DEFAULT_PAGE_SIZE = 10;

export type ChartsFilters = {
    aggregations: IndicatorFilterT<Aggregation>[];
    integrations: IndicatorFilterT<string>[];
    metrics: string[];
    title?: string;
    vizTypes: IndicatorFilterT<VizType>[];
};

export type ChartsQueryResponse = {
    charts: IndicatorT[];
    count: number;
};

export type ChartsTemplatesQueryResponse = {
    charts: LibraryChartTemplateT[];
    count: number;
};

const mapFilterValues = <T extends string>(
    filters: IndicatorFilterT<T>[],
): T[] => filters.map((filter) => filter.value);

/**
 * Preprocesses the filters to match the backend format
 * - We keep the value of each filter for aggregations, integrations and vizTypes
 * - We remove the filters that do not contain anything
 * @param filters the provided charts filters
 * @returns understandable body for the backend
 */
const preprocessChartsRequestBody = (filters: ChartsFilters) => {
    const { aggregations, integrations, metrics, title, vizTypes } = filters;

    const mappedFilters = {
        aggregations: mapFilterValues(aggregations),
        integrations: mapFilterValues(integrations),
        title,
        metrics,
        vizTypes: mapFilterValues(vizTypes),
    };
    return Object.fromEntries(
        Object.entries(mappedFilters).filter(
            ([, val]) => val && val.length > 0,
        ),
    );
};

export const useGetLibraryCharts = (
    chartsFilters: ChartsFilters,
    page: number,
): UseQueryResult<ChartsQueryResponse> =>
    useQuery({
        queryKey: ['charts', chartsFilters, page],

        queryFn: async () => {
            const response = await RQAPICallWithData<ChartsQueryResponse>({
                endpoint: `explore/charts/v2?page_index=${page}&page_size=${DEFAULT_PAGE_SIZE}`,
                body: preprocessChartsRequestBody(chartsFilters),
                method: 'POST',
            });
            return response.content.data;
        },
    });

export const useGetLibraryChartsTemplates = (
    chartsFilters: ChartsFilters,
    page: number,
): UseQueryResult<ChartsTemplatesQueryResponse> =>
    useQuery({
        queryKey: ['charts-templates', chartsFilters, page],

        queryFn: async () => {
            const response =
                await RQAPICallWithData<ChartsTemplatesQueryResponse>({
                    endpoint: `explore/charts/templates?page_index=${page}&page_size=${DEFAULT_PAGE_SIZE}`,
                    body: preprocessChartsRequestBody(chartsFilters),
                    method: 'POST',
                });
            return response.content.data;
        },
    });
